import React, { useState, useEffect } from "react";
import Dialog from "../../dialog/Dialog";
import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { ADMIN_PAGE_META } from "../../common/adminPageMeta";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as stateService from "./service";
import useTableSearch from "../../../hooks/useTableSearch";
import FilePreview from "../../FilePreview/FilePreview";
import PlaceSearchInput from "../../common/PlaceSearchInput";
import ToggleButton from "../../common/ToggleButton";
import ImageInput from "../../common/ImageInput";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableCell,
  AdminTableFooter,
  AdminTableToolbar,
  TableSearchInput,
  TableEmptyRow,
  EditActionButton,
  DeleteActionButton,
  DragHandleButton,
  TableActionsCell,
  StatusBadge,
  getStatusVariant,
} from "../../common/adminTable";

const StateThumbPreview = ({ state }) => {
  const [hasError, setHasError] = React.useState(false);
  const hasImage = state?.thumb_image_url && !hasError;

  if (!hasImage) {
    return (
      <div
        className="flex h-7 w-14 items-center justify-center rounded-md border border-dashed border-gray-200 bg-gray-50 text-[10px] font-medium text-gray-400"
        title="No image"
      >
        No image
      </div>
    );
  }

  return (
    <img
      src={state.thumb_image_url}
      alt="State preview"
      className="h-7 w-7 object-cover rounded-md"
      onError={() => setHasError(true)}
    />
  );
};

// Add SortableRow component
const SortableRow = ({
  state,
  index,
  moveRow,
  handleEditClick,
  handleDeleteClick,
  handleDragEnd,
}) => {
  const ref = React.useRef(null);

  const [{ isDragging }, drag] = useDrag({
    type: "row",
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (item, monitor) => {
      if (monitor.didDrop()) {
        handleDragEnd();
      }
    },
  });

  const [, drop] = useDrop({
    accept: "row",
    hover(item, monitor) {
      if (!ref.current) return;
      
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveRow(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  drag(drop(ref));

  return (
    <tr
      ref={ref}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      className={`hover:bg-gray-50 ${isDragging ? "bg-gray-100 shadow-lg" : ""}`}
    >
      <AdminTableCell>{index + 1}</AdminTableCell>
      <AdminTableCell>
        <StateThumbPreview state={state} />
      </AdminTableCell>
      <AdminTableCell>{state.name}</AdminTableCell>
      <AdminTableCell>{state.country?.name || "N/A"}</AdminTableCell>
      <AdminTableCell>
        <StatusBadge variant={getStatusVariant(state.status)}>
          {state.status === 1 ? "Active" : "In-Active"}
        </StatusBadge>
      </AdminTableCell>
      <AdminTableCell>
        <StatusBadge variant={state.display_web === 1 ? "active" : "inactive"}>
          {state.display_web === 1 ? "Shown" : "Hidden"}
        </StatusBadge>
      </AdminTableCell>
      <TableActionsCell>
        <EditActionButton
          showLabel={false}
          onClick={() => handleEditClick(state.id)}
        />
        <DeleteActionButton
          showLabel={false}
          onClick={() => handleDeleteClick(state)}
        />
        <DragHandleButton isDragging={isDragging} />
      </TableActionsCell>
    </tr>
  );
};

const States = () => {
  const queryClient = useQueryClient();
  const [orderedStates, setOrderedStates] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);

  // State management
  const [selectedState, setSelectedState] = useState(null);
  const { searchQuery, setSearchQuery, activeSearch, clearSearch } =
    useTableSearch();
  const [stateName, setStateName] = useState("");
  const [country, setCountry] = useState({
    id: "",
    name: "",
    code: "",
  });
  const [thumbImage, setThumbImage] = useState(null);
  const [coverImage, setCoverImage] = useState(null);
  const [stateStatus, setStateStatus] = useState(1);
  const [displayWeb, setDisplayWeb] = useState(true);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [stateLatitude, setStateLatitude] = useState("");
  const [stateLongitude, setStateLongitude] = useState("");
  const [statePlaceId, setStatePlaceId] = useState("");
 
  const [errors, setErrors] = useState({
    name: "",
    country: "",
    thumbImage: "",
    coverImage: "",
  });

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch]);

  // Queries
  const { data: stateData } = useQuery({
    queryKey: ["states", currentPage, activeSearch],
    queryFn: () =>
      stateService.list({ page: currentPage, search: activeSearch || undefined }),
    keepPreviousData: true,
  });

  const { data: countryData } = useQuery({
    queryKey: ["countryOptions"],
    queryFn: stateService.getCountryOptions,
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: stateService.create,
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(success.response?.data?.message || "State added successfully!");
      setAddDialogOpen(false);
      resetForm();
      queryClient.invalidateQueries(["states"]);
    },
    onError: (error) => {
      setIsLoading(false);
      setErrorMessage(error.response?.data?.message || "Failed to add state");
      setShowError(true);
    },
  });

  const updateMutation = useMutation({
    mutationFn: stateService.update,
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(success.response?.data?.message || "State updated successfully!");
      setEditDialogOpen(false);
      resetForm();
      queryClient.invalidateQueries(["states"]);
    },
    onError: (error) => {
      setIsLoading(false);
      setErrorMessage(error.response?.data?.message || "Failed to update state");
      setShowError(true);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: stateService.deleteState,
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(success.response?.data?.message || "State deleted successfully!");
      setDeleteDialogOpen(false);
      queryClient.invalidateQueries(["states"]);
    },
    onError: (error) => {
      setIsLoading(false);
      setErrorMessage(error.response?.data?.message || "Failed to delete state");
      setShowError(true);
    },
  });

  const getByIdMutation = useMutation({
    mutationFn: stateService.getById,
    onSuccess: (data) => {
      setIsLoading(false);
      setSelectedState(data.data);
      setStateName(data.data.name);
      const findCountry = countries.find(country => Number(country.id) === Number(data.data.country_id));
      setCountry({ id: findCountry?.id || "", name: findCountry?.name || "", code: findCountry?.code || "" });
      setStateStatus(data.data.status);
      setThumbImage(null);
      setCoverImage(null);
      setStateLatitude(data.data.latitude);
      setStateLongitude(data.data.longitude);
      setStatePlaceId(data.data.place_id);
      setDisplayWeb(data.data.display_web === 1);
      setEditDialogOpen(true);
    },
    onError: (error) => {
      setIsLoading(false);
      showErrorMessage(error.message || "Failed to fetch state details");
    },
  });

  // Update orderedStates when states change
  useEffect(() => {
    if (stateData?.data?.data) {
      // Sort states based on sort_order before setting state
      const sortedStates = [...stateData.data.data].sort((a, b) => {
        const orderA = a.sort_order ?? Number.MAX_SAFE_INTEGER;
        const orderB = b.sort_order ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
      setOrderedStates(sortedStates);
    }
  }, [stateData]);

  // Add sort mutation
  const sortMutation = useMutation({
    
    mutationFn: stateService.sort,
    
    onSuccess: (success) => {
      showSuccessMessage(success.response?.data?.message || "States reordered successfully!");
      queryClient.invalidateQueries(["states"]);
    },
    onError: (error) => {
      setErrorMessage(error.response?.data?.message || "Failed to reorder states");
      setShowError(true);
    },
  });

  const moveRow = (dragIndex, hoverIndex) => {
    setDragIndex(dragIndex);
    setHoverIndex(hoverIndex);
    const draggedState = orderedStates[dragIndex];
    const updatedStates = [...orderedStates];
    updatedStates.splice(dragIndex, 1);
    updatedStates.splice(hoverIndex, 0, draggedState);
    setOrderedStates(updatedStates);
  };

  const handleDragEnd = () => {
    if (dragIndex !== null && hoverIndex !== null) {
      const sort_from = orderedStates[dragIndex]?.id;
      const sort_to = orderedStates[hoverIndex]?.id;
      if (sort_from && sort_to) {
        sortMutation.mutate({ sort_from, sort_to });
      }
    }
    setDragIndex(null);
    setHoverIndex(null);
  };

  // Helper functions
  const resetForm = () => {
    setStateName("");
    setCountry("");
    setThumbImage(null);
    setCoverImage(null);
    setStateLatitude("");
    setStateLongitude("");
    setErrors({
      name: "",
      country: "",
      thumbImage: "",
      coverImage: "",
    });
  };

  const validateForm = () => {
    let tempErrors = {
      name: "",
      country: "",
      thumbImage: "",
      coverImage: "",
    };
    let isValid = true;

    if (!stateName.trim()) {
      tempErrors.name = "State name is required";
      isValid = false;
    }

    if (!country) {
      tempErrors.country = "Please select a country";
      isValid = false;
    }

    if (!thumbImage && !selectedState) {
      tempErrors.thumbImage = "Thumb image is required";
      isValid = false;
    }

    if (!coverImage && !selectedState) {
      tempErrors.coverImage = "Cover image is required";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setSuccessMessage("");
    }, 3000);
  };

  const showErrorMessage = (errorMsg) => {
    setErrorMessage(errorMsg);
    setShowError(true);
    setTimeout(() => {
      setShowError(false);
      setErrorMessage("");
    }, 3000);
  };

  const handlePlaceSelect = (placeDetails) => {
    console.log(placeDetails);
    if (placeDetails) {
      setStateName(placeDetails.address_components[0].long_name);
      setStateLatitude(placeDetails.lat);
      setStateLongitude(placeDetails.lng);
      setStatePlaceId(placeDetails.place_id);
      setErrors({ ...errors, name: "" });
    } else {
      setStateName("");
      setStateLatitude("");
      setStateLongitude("");
    }
  };

  // Action handlers
  const handleAddClick = () => {
    resetForm();
    setStateStatus(1);
    setAddDialogOpen(true);
  };

  const handleDeleteClick = (state) => {
    setSelectedState(state);
    setDeleteDialogOpen(true);
  };

  const handleEditClick = (stateId) => {
    getByIdMutation.mutate(stateId);
  };

  const handleSaveState = () => {
    setIsLoading(true);
    if (validateForm()) {
      const formData = new FormData();
      formData.append("name", stateName);
      formData.append("country_id", country.id);
      formData.append("status", stateStatus);
      formData.append("latitude", stateLatitude);
      formData.append("longitude", stateLongitude);
      formData.append("place_id", statePlaceId);
      formData.append("display_web", displayWeb ? 1 : 0);

      if (thumbImage) {
        formData.append("thumb_image", thumbImage);
      }
      if (coverImage) {
        formData.append("cover_image", coverImage);
      }
      createMutation.mutate(formData);
    }else{
      setIsLoading(false);
    }
  };

  const handleUpdateState = () => {
    setIsLoading(true);
    if (validateForm()) {
      const formData = new FormData();
      formData.append("name", stateName);
      formData.append("country_id", country.id);
      formData.append("status", stateStatus);
      formData.append("latitude", stateLatitude);
      formData.append("longitude", stateLongitude);
      formData.append("place_id", statePlaceId);
      formData.append("display_web", displayWeb ? 1 : 0);

      if (thumbImage) {
        formData.append("thumb_image", thumbImage);
      }
      if (coverImage) {
        formData.append("cover_image", coverImage);
      }

      updateMutation.mutate({
        id: selectedState.id,
        formData,
      });
    }else{
      setIsLoading(false);
    }
  };

  const handleDeleteState = () => {
    setIsLoading(true);
    if (selectedState) {
      deleteMutation.mutate(selectedState.id);
    }
  };

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };


  const countries = countryData?.data || [];

  console.log(countries);

  const pagination = {
    currentPage: stateData?.data?.current_page || 1,
    lastPage: stateData?.data?.last_page || 1,
    perPage: stateData?.data?.per_page || 10,
    total: stateData?.data?.total || 0,
    from: stateData?.data?.from || 0,
    to: stateData?.data?.to || 0,
  };

  return (
    <AdminPageLayout>
      {/* Error Toster */}
      {showError && (
        <Toster
          message={errorMessage}
          type="error"
          duration={3000}
          onClose={() => setShowError(false)}
        />
      )}

      {/* Success Toster */}
      {showSuccess && successMessage && (
        <Toster
          message={successMessage}
          type="success"
          duration={3000}
          onClose={() => {
            setShowSuccess(false);
            setSuccessMessage("");
          }}
        />
      )}

      <AdminPageHeader
        title={ADMIN_PAGE_META.states.title}
        subtitle={ADMIN_PAGE_META.states.subtitle}
      />

      <AdminPageCard>
        <AdminPageCardToolbar>
          <AdminTableToolbar className="mb-0">
            <TableSearchInput
              placeholder="Search states..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <button
              onClick={handleAddClick}
              className="bg-primary text-white px-4 py-1 rounded-lg border border-primary shadow-md hover:bg-primary/90 transition-all"
            >
              Add State
            </button>
          </AdminTableToolbar>
        </AdminPageCardToolbar>

        <DndProvider backend={HTML5Backend}>
          <AdminTable
            className="shadow-none rounded-none"
            footer={
              <AdminTableFooter>
                <Pagination
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  showInfo={true}
                />
              </AdminTableFooter>
            }
          >
          <AdminTableHead>
            <AdminTableTh>#</AdminTableTh>
            <AdminTableTh>Preview</AdminTableTh>
            <AdminTableTh>Title</AdminTableTh>
            <AdminTableTh>Country</AdminTableTh>
            <AdminTableTh>Status</AdminTableTh>
            <AdminTableTh>Display</AdminTableTh>
            <AdminTableTh align="right">Actions</AdminTableTh>
          </AdminTableHead>
          <AdminTableBody>
            {orderedStates.length > 0 ? (
              orderedStates.map((state, index) => (
                <SortableRow
                  key={state.id}
                  state={state}
                  index={index}
                  moveRow={moveRow}
                  handleEditClick={handleEditClick}
                  handleDeleteClick={handleDeleteClick}
                  handleDragEnd={handleDragEnd}
                />
              ))
            ) : (
              <TableEmptyRow
                colSpan={7}
                emptyMessage="No states found"
                searchQuery={activeSearch}
                onClearSearch={clearSearch}
              />
            )}
          </AdminTableBody>
        </AdminTable>
      </DndProvider>
      </AdminPageCard>

      {/* Add State Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
          setErrors({
            name: "",
            country: "",
            thumbImage: "",
            coverImage: "",
          });
        }}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-bs-add"></i>
            Create State
          </div>
        }
        primaryButtonText="Save"
        primaryButtonAction={handleSaveState}
        closeButtonText="Close"
        closeButtonAction={() => {
          setAddDialogOpen(false);
          setErrors({
            name: "",
            country: "",
            thumbImage: "",
            coverImage: "",
          });
        }}
        isLoading={isLoading}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country <span className="text-red-500">*</span>
              </label>
              <select
                className={`text-sm w-full px-3 py-1 border ${errors.country ? "border-red-500" : "border-gray-300"
                  } rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary`}
                value={country.id}
                onChange={(e) => {
                  const findCountry = countries.find(country => Number(country.id) === Number(e.target.value));

                  console.log("Selected country:", e.target.value);
                  setCountry({
                    id: findCountry?.id || "",
                    name: findCountry?.name || "",
                    code: findCountry?.code || "",
                  
                  });
                  // clear state 
                  setStateName("");
                  setStateLatitude("");
                  setStateLongitude("");
                  setStatePlaceId("");
                  setErrors({ ...errors, country: "" });
                }}
              >
                <option value="">Select a country</option>
                {countries.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </select>
              {errors.country && (
                <p className="text-red-500 text-xs mt-1">{errors.country}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State Name <span className="text-red-500">*</span>
              </label>
              
              <PlaceSearchInput
                isDisabled={country.id === ""}
                value={stateName}
                onChange={handlePlaceSelect}
                placeholder="Search for an Indian state..."
                hasError={!!errors.name}
                types={["administrative_area_level_1"]}
                componentRestrictions={{ country: country.code }}
              />
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
              )}
            </div>
            
          </div>

          <div className="flex flex-row gap-4">
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  className="text-sm w-full px-3 grid-cols-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  value={stateStatus}
                  onChange={(e) => setStateStatus(Number(e.target.value))}
                >
                  <option value={1}>Active</option>
                  <option value={0}>In-Active</option>
                </select>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <ToggleButton
              enabled={displayWeb}
              onChange={setDisplayWeb}
              label="Show state on website?"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <ImageInput
                onChange={(file) => {
                  setThumbImage(file);
                  setErrors({ ...errors, thumbImage: "" });
                }}
                showLabel={true}
                label="Thumbnail Image"
                required={true}
                error={errors.thumbImage}
              />
              {errors.thumbImage && (
                <p className="text-red-500 text-xs mt-1">{errors.thumbImage}</p>
              )}
            </div>

            <div>
              <ImageInput
                onChange={(file) => {
                  setCoverImage(file);
                  setErrors({ ...errors, coverImage: "" });
                }}
                showLabel={true}
                label="Cover Image"
                required={true}
                error={errors.coverImage}
              />
              {errors.coverImage && (
                <p className="text-red-500 text-xs mt-1">{errors.coverImage}</p>
              )}
            </div>
          </div>

          {/* Preview Section */}
          <div className="mt-4">
            <label className="block font-bold text-sm text-gray-700 mb-1">
              Attachment preview
            </label>
            <div className="grid grid-cols-2 gap-4">
              {thumbImage ? (
                <FilePreview
                  file={thumbImage}
                  label="Thumbnail Image"
                  onDelete={() => setThumbImage(null)}
                  isImage={true}
                  showDelete={false}
                />
              ) : (
                <div className="text-sm text-gray-500">No thumbnail image selected</div>
              )}
              {coverImage ? (
                <FilePreview
                  file={coverImage}
                  label="Cover Image"
                  onDelete={() => setCoverImage(null)}
                  isImage={true}
                  showDelete={false}
                />
              ) : (
                <div className="text-sm text-gray-500">No cover image selected</div>
              )}
            </div>
          </div>
        </div>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-pencil text-[16px]"></i>
            Edit State
          </div>
        }
        primaryButtonText="Update"
        primaryButtonAction={handleUpdateState}
        closeButtonText="Cancel"
        closeButtonAction={() => setEditDialogOpen(false)}
        isLoading={isLoading}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country <span className="text-red-500">*</span>
              </label>
              <select
                className={`text-sm w-full px-3 py-1 border ${errors.country ? "border-red-500" : "border-gray-300"
                  } rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary`}
                value={country.id}
                onChange={(e) => {
                  const findCountry = countries.find(country => Number(country.id) === Number(e.target.value));
      
                  setCountry({
                    id: findCountry?.id || "",
                    name: findCountry?.name || "",
                    code: findCountry?.code || "",
                  
                  });
                  // clear state 
                  setStateName("");
                  setStateLatitude("");
                  setStateLongitude("");
                  setStatePlaceId("");
                  setErrors({ ...errors, country: "" });
                }}
              >
                <option value="">Select a country</option>
                {countries.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </select>
              {errors.country && (
                <p className="text-red-500 text-xs mt-1">{errors.country}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State Name <span className="text-red-500">*</span>
              </label>
              <PlaceSearchInput
                value={stateName}
                onChange={handlePlaceSelect}
                placeholder="Search for an Indian state..."
                hasError={!!errors.name}
                types={["administrative_area_level_1"]}
                componentRestrictions={{ country: country.code }}
              />
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
              )}
            </div>
          
          </div>

          <div className="flex flex-row gap-4">
            <div>
              <div className="mb-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  className="text-sm w-full px-3 grid-cols-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  value={stateStatus}
                  onChange={(e) => setStateStatus(Number(e.target.value))}
                >
                  <option value={1}>Active</option>
                  <option value={0}>In-Active</option>
                </select>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <ToggleButton
              enabled={displayWeb}
              onChange={setDisplayWeb}
              label="Show state on website?"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <ImageInput
                onChange={(file) => {
                  setThumbImage(file);
                  setErrors({ ...errors, thumbImage: "" });
                }}
                showLabel={true}
                label="Thumbnail Image"
                error={errors.thumbImage}
              />
              {errors.thumbImage && (
                <p className="text-red-500 text-xs mt-1">{errors.thumbImage}</p>
              )}
            </div>

            <div>
              <ImageInput
                onChange={(file) => {
                  setCoverImage(file);
                  setErrors({ ...errors, coverImage: "" });
                }}
                label="Cover Image"
                showLabel={true}
                error={errors.coverImage}
              />
              {errors.coverImage && (
                <p className="text-red-500 text-xs mt-1">{errors.coverImage}</p>
              )}
            </div>
          </div>

          {/* Preview Section */}
          <div>
            <label className="block font-bold text-sm text-gray-700 mb-1">
              Attachment preview
            </label>
            <div className="grid grid-cols-2 gap-4">
              {thumbImage ? (
                <FilePreview
                  file={thumbImage}
                  label="Thumbnail Image"
                  onDelete={() => setThumbImage(null)}
                  isImage={true}
                  showDelete={true}
                />
              ) : selectedState && selectedState.thumb_image_url ? (
                <FilePreview
                  fileUrl={selectedState.thumb_image_url}
                  label="Current Thumbnail Image"
                  isImage={true}
                  showDelete={true}
                />
              ) : (
                <div className="text-sm text-gray-500">
                  No thumb image selected
                </div>
              )}

              {coverImage ? (
                <FilePreview
                  file={coverImage}
                  label="Cover Image"
                  onDelete={() => setCoverImage(null)}
                  isImage={true}
                  showDelete={true}
                />
              ) : selectedState && selectedState.cover_image_url ? (
                <FilePreview
                  fileUrl={selectedState.cover_image_url}
                  label="Current Cover Image"
                  isImage={true}
                  showDelete={true}
                />
              ) : (
                <div className="text-sm text-gray-500">
                  No cover image selected
                </div>
              )}
            </div>
          </div>
        </div>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-trash text-[16px]"></i>
            Delete State
          </div>
        }
        primaryButtonText="Delete"
        primaryButtonAction={handleDeleteState}
        closeButtonText="Cancel"
        closeButtonAction={() => setDeleteDialogOpen(false)}
        isLoading={isLoading}
      >
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            Are you sure you want to delete this state? This action cannot be
            undone.
          </p>
        </div>
      </Dialog>
    </AdminPageLayout>
  );
};

export default States;
